import React from "react";
import { motion, useTransform } from "framer-motion";

/**
 * MethodThread — ONE elongated female figure in a long flowing coat and
 * wide-brimmed hat, drawn as a loose continuous-line fashion illustration.
 * Exclusive to the Method page; imported only by MethodPage.jsx.
 *
 * The figure spans the opening section and all five movements (one body part
 * per zone of the 400×2400 viewBox): hat + head (opening), neck/shoulders and
 * the collar's deep V (01 Observe), torso/lapels/arm (02 Edit), knotted belt
 * (03 Structure), coat folds + leg (04 Refine), flaring hem + trailing line
 * (05 Sustain). 14 long continuous paths — no ticks, dashes or closed ovals.
 * Strokes cross zone boundaries as single unbroken paths, so the figure reads
 * as one drawing while scrolling. No face, no fills, 1px warm-grey strokes.
 *
 * The scroll-linked draw-in mechanism is unchanged: a shared scroll progress
 * drives each path's pathLength within its own window.
 */

const STROKE = "#94968f"; // warm grey — reads on ivory, lighter on the slate Edit band

/* ── Opening section · hat brim (one long sweep) + open head loop, no face ── */
const HAT = "M 18 138 C 130 72, 285 70, 382 148";
const HEAD = "M 168 168 C 152 215, 158 285, 198 318 C 232 345, 262 312, 256 262 C 251 218, 230 184, 206 170";

/* ── 01 Observe → 02 Edit · neck → shoulder → deep collar V → lapel ── */
const LEFT_LINE =
  "M 176 330 C 172 420, 152 498, 110 560 C 150 548, 178 556, 196 612 " +
  "C 211 662, 224 722, 206 790 C 172 852, 132 922, 120 1012 C 112 1078, 120 1132, 142 1182";
const RIGHT_LINE =
  "M 242 326 C 246 420, 268 498, 306 558 C 262 546, 232 554, 214 610 " +
  "C 200 660, 188 722, 206 790 C 244 856, 284 926, 296 1016 C 303 1080, 295 1136, 272 1186";

/* ── 01 Observe → 05 Sustain · the coat's two long outer edges ──────── */
const COAT_L = "M 104 575 C 82 720, 96 920, 86 1110 C 76 1300, 110 1450, 94 1600 C 80 1750, 102 1920, 84 2052";
const COAT_R = "M 308 570 C 330 725, 314 925, 326 1115 C 336 1300, 302 1460, 318 1610 C 332 1760, 306 1925, 326 2062";

/* ── 02 Edit · torso inside the open coat, one arm along the body ───── */
const TORSO = "M 206 800 C 196 905, 212 1005, 201 1105 C 196 1155, 201 1195, 207 1215";
const ARM = "M 306 560 C 338 655, 348 785, 337 905 C 329 1005, 322 1105, 332 1185 C 336 1215, 344 1235, 353 1246";

/* ── 03 Structure · belt wrapping the waist into a loose open knot, ends fall ── */
const BELT =
  "M 105 1298 C 165 1272, 255 1272, 308 1302 C 272 1288, 240 1292, 222 1308 " +
  "C 208 1320, 214 1340, 234 1338 C 250 1336, 256 1320, 246 1310 " +
  "C 236 1360, 220 1442, 208 1560";
const BELT_END = "M 237 1342 C 252 1412, 236 1492, 254 1568";

/* ── 04 Refine · long coat folds (continuous drapery lines) + one leg ── */
const FOLDS_A = "M 132 1612 C 122 1722, 142 1832, 127 1970 C 130 1986, 186 1988, 201 1980 C 206 1842, 186 1722, 197 1602";
const FOLD3_LEG = "M 266 1616 C 256 1732, 276 1846, 261 1986 C 256 1999, 231 1999, 225 1991 C 233 1862, 223 1742, 229 1622";

/* ── 05 Sustain · hem flares wide between the coat edges, then trails off ── */
const HEM = "M 84 2052 C 56 2142, 118 2218, 208 2230 C 298 2242, 352 2176, 326 2062";
const TRAIL = "M 208 2230 C 198 2292, 215 2342, 190 2398";

// [path, opacity, scroll window] — one entry per stroke of the figure.
const FIGURE = [
  [HAT, 0.62, [0.0, 0.07]],
  [HEAD, 0.6, [0.02, 0.1]],
  [LEFT_LINE, 0.6, [0.07, 0.36]],
  [RIGHT_LINE, 0.6, [0.07, 0.36]],
  [COAT_L, 0.55, [0.1, 0.88]],
  [COAT_R, 0.55, [0.1, 0.88]],
  [TORSO, 0.45, [0.28, 0.4]],
  [ARM, 0.5, [0.28, 0.42]],
  [BELT, 0.6, [0.42, 0.56]],
  [BELT_END, 0.5, [0.48, 0.58]],
  [FOLDS_A, 0.55, [0.58, 0.74]],
  [FOLD3_LEG, 0.55, [0.6, 0.76]],
  [HEM, 0.6, [0.8, 0.93]],
  [TRAIL, 0.45, [0.9, 1.0]],
];

const DrawnPath = ({ d, o, win, progress }) => {
  const len = useTransform(progress, win, [0, 1]);
  return (
    <motion.path
      d={d}
      stroke={STROKE}
      strokeWidth={1}
      strokeOpacity={o}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      vectorEffect="non-scaling-stroke"
      style={{ pathLength: len }}
    />
  );
};

const MethodThread = ({ progress }) => (
  <div
    aria-hidden="true"
    data-testid="method-thread"
    className="hidden min-[1280px]:block absolute inset-y-0 right-0 w-[clamp(300px,30vw,480px)] pointer-events-none z-0"
  >
    <svg viewBox="0 0 400 2400" preserveAspectRatio="none" className="w-full h-full" fill="none">
      {FIGURE.map(([d, o, win]) => (
        <DrawnPath key={d.slice(0, 24)} d={d} o={o} win={win} progress={progress} />
      ))}
    </svg>
  </div>
);

/**
 * MethodThreadMobile — unchanged mobile behaviour (<768px): a small distilled
 * field of fold-lines below the section label, clear of all text.
 */
const MOBILE_LINES = [
  "M 10 30 C 80 10, 150 50, 220 28 C 270 12, 310 30, 330 22",
  "M 20 70 C 90 50, 170 90, 240 66 C 290 50, 320 70, 335 62",
  "M 30 118 C 100 96, 165 130, 235 108 C 285 94, 315 112, 330 104",
  "M 150 55 C 120 45, 105 75, 130 92 C 155 108, 195 95, 188 68 C 183 50, 162 48, 150 55",
  "M 60 18 c -10 0 -10 15 0 15 c 8 0 10 -6 9 -11 M 69 22 C 92 34, 110 44, 118 58",
];

export const MethodThreadMobile = () => (
  <svg
    aria-hidden="true"
    data-testid="method-thread-mobile"
    viewBox="0 0 340 150"
    className="md:hidden w-full max-w-[340px] h-auto mt-8"
    fill="none"
  >
    {MOBILE_LINES.map((d, i) => (
      <motion.path
        key={i}
        d={d}
        stroke={STROKE}
        strokeWidth={0.7}
        strokeOpacity={0.55}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={{ duration: 1.8, delay: i * 0.18, ease: [0.16, 1, 0.3, 1] }}
      />
    ))}
  </svg>
);

export default MethodThread;
